import { useParams, Link, useNavigate } from "react-router-dom";
import { findAnimal, ANIMALS } from "@/data/animals";
import { ArrowLeft, ArrowRight, MapPinLine, Ruler, ForkKnife, Tree } from "@phosphor-icons/react";
import { Editable } from "@/components/Editable";

export default function AnimalProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const animal = findAnimal(id);

  if (!animal) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="not-found">
        <div className="font-mono-label text-[11px] text-[#5C6B5E]">404 · Unknown specimen</div>
        <h1 className="font-serif-display text-5xl text-[#2A3A2C] mt-3">No such creature in the guide.</h1>
        <Link to="/" className="link-quill text-[#2A3A2C] font-mono-label text-[11px] mt-6 inline-block">
          ← Return to the field guide
        </Link>
      </div>
    );
  }

  const isMystery = animal.mystery;
  const idx = ANIMALS.findIndex((a) => a.id === animal.id);
  const next = ANIMALS[(idx + 1) % ANIMALS.length];
  const prev = ANIMALS[(idx - 1 + ANIMALS.length) % ANIMALS.length];

  const palette = isMystery
    ? {
        bg: "bg-[#1A1C19]",
        text: "text-[#F4F1EA]",
        muted: "text-[#D1C9B8]/70",
        border: "border-[#F4F1EA]/15",
        chipBg: "bg-[#B75D3F] text-white",
        rule: "bg-[#F4F1EA]/15",
      }
    : {
        bg: "bg-[#F4F1EA]",
        text: "text-[#2A3A2C]",
        muted: "text-[#5C6B5E]",
        border: "border-[#D1C9B8]",
        chipBg: "bg-[#2A3A2C] text-[#F4F1EA]",
        rule: "bg-[#D1C9B8]",
      };

  return (
    <div className={`${palette.bg} ${palette.text} min-h-screen`} data-testid={`profile-${animal.id}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10">
        <button
          onClick={() => navigate(-1)}
          data-testid="profile-back-btn"
          className={`inline-flex items-center gap-2 font-mono-label text-[10px] ${palette.muted} hover:opacity-70`}
        >
          <ArrowLeft size={14} /> Back to the guide
        </button>
      </div>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-8 grid md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-5">
          <div className="aspect-[3/4] overflow-hidden border border-current/10 relative">
            <img
              src={animal.image}
              alt={animal.name}
              className="w-full h-full object-cover"
            />
            {isMystery && <div className="absolute inset-0 bg-black/30" />}
          </div>
          <div className={`mt-3 flex justify-between font-mono-label text-[10px] ${palette.muted}`}>
            <span>Plate No. {animal.number}</span>
            <span>{animal.category}</span>
          </div>
        </div>

        <div className="md:col-span-7">
          <div className={`inline-block font-mono-label text-[10px] px-2 py-1 ${palette.chipBg}`}>
            {isMystery ? "Unverified Species" : "Species Profile"}
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl mt-5 leading-[0.95]">
            {animal.name}
          </h1>
          <p className={`mt-3 italic ${palette.muted}`}>{animal.scientific}</p>

          <div className={`h-px w-full ${palette.rule} my-8`} />

          <p className="text-lg md:text-xl leading-relaxed font-serif-display">
            <Editable id={`animal.${animal.id}.blurb`} multiline defaultValue={animal.blurb} />
          </p>

          {/* Stat blocks */}
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            <StatBlock icon={<Tree size={16} />} label="Habitat" contentId={`animal.${animal.id}.habitat`} value={animal.habitat} palette={palette} />
            <StatBlock icon={<ForkKnife size={16} />} label="Diet" contentId={`animal.${animal.id}.diet`} value={animal.diet} palette={palette} />
            <StatBlock icon={<Ruler size={16} />} label="Size" contentId={`animal.${animal.id}.size`} value={animal.size} palette={palette} />
            <StatBlock icon={<MapPinLine size={16} />} label="Status" contentId={`animal.${animal.id}.status`} value={animal.status} palette={palette} />
          </div>
        </div>
      </section>

      {/* FUN FACTS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24 md:mt-32">
        <div className="font-mono-label text-[11px]" style={{ color: isMystery ? "#B75D3F" : "#5C6B5E" }}>
          § Field Notes
        </div>
        <h2 className="font-serif-display text-4xl md:text-5xl mt-3">
          {isMystery ? "Points to consider." : "Fun facts."}
        </h2>
        <div className={`h-px w-full ${palette.rule} my-8`} />

        <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
          {animal.facts.map((fact, i) => (
            <div
              key={i}
              data-testid={`fact-${i}`}
              className={`flex gap-5 pb-6 border-b ${palette.border}`}
            >
              <div className={`font-serif-display text-4xl leading-none ${palette.muted} italic`}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <p className="text-lg leading-relaxed pt-1">
                <Editable id={`animal.${animal.id}.fact.${i}`} multiline defaultValue={fact} />
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Prev / Next */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24 pb-24 grid sm:grid-cols-2 gap-6">
        <Link
          to={`/species/${prev.id}`}
          data-testid="profile-prev"
          className={`group border ${palette.border} p-6 flex items-center gap-4 hover:opacity-90`}
        >
          <ArrowLeft size={18} />
          <div>
            <div className={`font-mono-label text-[10px] ${palette.muted}`}>Previous Plate</div>
            <div className="font-serif-display text-2xl mt-1">{prev.name}</div>
          </div>
        </Link>
        <Link
          to={`/species/${next.id}`}
          data-testid="profile-next"
          className={`group border ${palette.border} p-6 flex items-center justify-end gap-4 text-right hover:opacity-90`}
        >
          <div>
            <div className={`font-mono-label text-[10px] ${palette.muted}`}>Next Plate</div>
            <div className="font-serif-display text-2xl mt-1">{next.name}</div>
          </div>
          <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}

function StatBlock({ icon, label, value, palette, contentId }) {
  return (
    <div className={`border-l-2 ${palette.border} pl-4`}>
      <div className={`flex items-center gap-2 font-mono-label text-[10px] ${palette.muted}`}>
        {icon} {label}
      </div>
      <div className="mt-2 text-base leading-snug">
        {contentId ? <Editable id={contentId} defaultValue={value} /> : value}
      </div>
    </div>
  );
}
